import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  input,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { SortMeta } from "primeng/api";
import { Subscription } from "rxjs";
import { skip } from "rxjs/operators";
import { quoteStatus } from "../quote-form/quote-status.const";
import { QuoteRepository } from "../quote.repository";
import { ActivatedRoute, Router } from "@angular/router";
import { Quote } from "../quote.type";
import { GridParams } from "../../shared/grid/grid-params";
import { GridSortingService } from "../../shared/grid/grid-sorting.service";
import { role } from "../../user/role.const";
import { User } from "../../user/user.model";
import { PaginationList } from "../../shared/grid/pagination-list.model";
import { NotifyService } from "../../notify/notify.service";
import { UserService } from "../../user/user.service";
import { noop } from "../../shared/noop.function";
import { ORDER } from "../../shared/grid/order.enum";
import { TOAST } from "../../notify/toast/toast-type.enum";
import { BackendErrorPipe } from "../../shared/backend/backend-error.pipe";
import { quoteListParams } from "./quote-list-params.const";
import { quoteSource } from "../quote-source.const";
import { quoteListParamsDefaults } from "./quote-list-params-defaults.const";
import { IRequestInfo } from "../../shared/request-info/request-info.interface";
import { requestInfoType } from "../../shared/request-info/request-info-type.const";
import { Client } from "../../client/client.model";
import { tableColumn } from "../../ui/table/table-column.const";
import { quoteListFilter } from "./quote-list-filter/quote-list-filter.const";
import { clientType } from "app/client/client-type.const";
import { Company } from "app/company/company.model";
import { NgTemplateOutlet } from "@angular/common";
import { NgxPermissionsModule } from "ngx-permissions";
import { PageTitleComponent } from "../../ui/page-title/page-title.component";
import { QuoteListFilterComponent } from "./quote-list-filter/quote-list-filter.component";
import { QuoteVariantMultiselectComponent } from "../../ui/dropdown/quote-variant-multiselect/quote-variant-multiselect.component";
import { QuoteDirectionSelectComponent } from "../../ui/dropdown/quote-direction-select/quote-direction-select.component";
import { QuoteStatusMultiselectComponent } from "../../ui/dropdown/quote-status-multiselect/quote-status-multiselect.component";
import { Bind } from "primeng/bind";
import { CompanyMultiselectComponent } from "../../ui/dropdown/company-multiselect/company-multiselect.component";
import { ButtonDirective } from "primeng/button";
import {
  QuoteSourceMultiselectComponent,
  QuoteSourceValue,
} from "../../ui/dropdown/quote-source-multiselect/quote-source-multiselect.component";
import { QuoteListBatchActionsComponent } from "./quote-list-batch-actions/quote-list-batch-actions.component";
import { QuoteBatchMailEditorComponent } from "../quote-batch-mail-editor/quote-batch-mail-editor.component";
import { QuoteEmail } from "../models/email.model";
import { DateRange } from "../../shared/date/date-range.model";
import { DatePickerComponent } from "../../ui/date-picker/date-picker.component";
import { TableComponent } from "../../ui/table/table.component";
import { TableSelectionBarComponent } from "../../ui/table-selection-bar/table-selection-bar.component";
import { SearchBoxComponent } from "../../ui/search-box/search-box.component";
import { QuoteListTabsComponent } from "../quote-list-tabs/quote-list-tabs.component";

@Component({
  selector: "app-quote-list",
  templateUrl: "./quote-list.component.html",
  styleUrls: ["./quote-list.component.scss"],
  providers: [
    GridParams,
    {
      provide: GridParams.DEFAULTS,
      useValue: quoteListParamsDefaults,
    },
    {
      provide: GridParams.STORAGE_KEY,
      useValue: "quote-list-new",
    },
    GridSortingService,
    {
      provide: GridSortingService.SORTING_FIELDS,
      useValue: ["status", "updatedAt"],
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgTemplateOutlet,
    NgxPermissionsModule,
    PageTitleComponent,
    QuoteListFilterComponent,
    QuoteVariantMultiselectComponent,
    QuoteDirectionSelectComponent,
    QuoteStatusMultiselectComponent,
    Bind,
    CompanyMultiselectComponent,
    QuoteListBatchActionsComponent,
    QuoteBatchMailEditorComponent,
    TableComponent,
    DatePickerComponent,
    QuoteSourceMultiselectComponent,
    ButtonDirective,
    SearchBoxComponent,
    QuoteListTabsComponent,
  ],
})
export class QuoteListComponent implements OnInit, OnDestroy {
  gridParams = inject(GridParams);
  protected quoteRepository = inject(QuoteRepository);
  protected sortingService = inject(GridSortingService);
  protected notifyService = inject(NotifyService);
  protected userService = inject(UserService);
  protected router = inject(Router);
  protected route = inject(ActivatedRoute);

  public readonly showFilters = input(true);
  public readonly showTitle = input(true);
  public readonly client = input<Client>(undefined);

  private readonly batchEditor = viewChild(QuoteBatchMailEditorComponent);
  private readonly batchActions = viewChild(QuoteListBatchActionsComponent);

  public role = role;
  public quoteStatus = quoteStatus;
  public user: User = null;
  public quoteList: PaginationList<Quote> = new PaginationList<Quote>();
  public selectedQuotes: Quote[] = [];
  public searchQuery = "";
  public quoteFilter: string = null;
  public companyIdsFilter: number[] = [];
  public selectedVariants: string[] = [];
  public selectedDirection: string;
  public createdAtRange: DateRange | null = null;
  public selectedQuoteStatuses: string[] = [];
  public selectedQuoteSources: QuoteSourceValue[] = [];
  public loading = false;
  public tableColumn = tableColumn;
  public contextMenuSelection: Quote;
  public contextMenuItems = [
    {
      label: "Open in new Tab",
      icon: "pi pi-external-link",
      command: () => this.openInNewTab(this.contextMenuSelection),
    },
  ];
  private refreshTimeout;
  // Quotes created by a user in the UI have no source; the backend criteria
  // filter matches those on the literal string "null".
  private static readonly NULL_QUOTE_SOURCE = "null";
  // Source values persisted by sessions from before the rename.
  private static readonly RENAMED_QUOTE_SOURCES: Record<string, string> = {
    sales_email: quoteSource.internalEmail,
    client_email: quoteSource.externalEmail,
  };
  private static readonly CLIENT_MODE_URL_FILTERS = [
    quoteListParams.quoteStatus,
    quoteListParams.createdAtFrom,
    quoteListParams.createdAtTo,
  ];

  protected subscription = new Subscription();
  protected fetchSubscription?: Subscription;

  ngOnInit() {
    this.userService.getUser().then((user) => {
      this.user = user;
      this.fetchQuotes();
    }, noop);

    this.loadClientParams();
    this.loadParams();

    this.subscription.add(
      this.gridParams.onChange.subscribe(() => this.fetchQuotes()),
    );

    // The client tab is built once and kept alive behind the sibling tabs, so a
    // drill-down that only adds query params never re-runs the init above.
    this.subscription.add(
      this.route.queryParams
        .pipe(skip(1))
        .subscribe(() => this.applyIncomingUrlFilters()),
    );
  }

  private applyIncomingUrlFilters(): void {
    if (!this.client() || !Object.keys(this.pickPreservedUrlParams()).length) {
      return;
    }

    this.loadClientParams();
    this.loadParams();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.resetPendingQuoteTimeout();
  }

  public onLoadPage(page: number, rows: number) {
    this.gridParams.setParams({
      [quoteListParams.page]: page,
      [quoteListParams.limit]: rows,
    });
  }

  public onSortGrid({ field, order }: SortMeta) {
    this.sortingService.sortGrid(field, ORDER[order]);
  }

  public onSelectRow(rowData: Quote) {
    rowData.requestInfo = null;
  }

  public onClickRow(event) {
    this.router.navigate([`/quote/${event.getId()}/${event.getDisplayId()}`]);
  }

  public onQuoteStatusSelectChange(statuses: string[]) {
    this.selectedQuoteStatuses = statuses ?? [];
    this.storeParams();
  }

  public onQuoteFilterChange(quoteFilter: string | null) {
    this.quoteFilter = quoteFilter;
    this.storeParams();
  }

  public onDirectionChange(direction) {
    this.selectedDirection = direction;
    this.storeParams();
  }

  public onCreatedAtRangeChange(range: DateRange | null): void {
    this.createdAtRange = range;
    const from = range?.getStart() ?? null;
    const to = range?.getEnd() ?? null;
    this.gridParams.setParams({
      [quoteListParams.createdAtFrom]: from
        ? this.toLocalDateString(from)
        : null,
      [quoteListParams.createdAtTo]: to ? this.toLocalDateString(to) : null,
      [quoteListParams.page]: 1,
    });
  }

  private toLocalDateString(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }

  private restoreSourceParam(): string | null {
    const stored: string | null = this.gridParams.getParam(
      quoteListParams.source,
    );
    if (!stored) {
      return stored;
    }

    const renamed = stored
      .split(",")
      .map(
        (source) => QuoteListComponent.RENAMED_QUOTE_SOURCES[source] ?? source,
      )
      .join(",");
    if (renamed !== stored) {
      this.gridParams.setParam(quoteListParams.source, renamed, false);
    }

    return renamed;
  }

  private parseLocalDate(value: string): Date {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  public onCompanyMultiselectChange(companies: Company[]) {
    this.companyIdsFilter = companies.map((company) => company.getId());
    this.gridParams.setParams({
      page: 1,
      "criteria[companies]":
        this.companyIdsFilter && this.companyIdsFilter.length
          ? this.companyIdsFilter.join(",")
          : null,
    });
  }

  public onQuoteSourcesChange(sources: QuoteSourceValue[]) {
    this.selectedQuoteSources = sources ?? [];
    this.gridParams.setParams({
      [quoteListParams.page]: 1,
      [quoteListParams.source]: this.selectedQuoteSources.length
        ? this.selectedQuoteSources
            .map((source) => source ?? QuoteListComponent.NULL_QUOTE_SOURCE)
            .join(",")
        : null,
    });
  }

  public onSearchQueryChange() {
    this.gridParams.setParams({
      search: this.searchQuery,
      page: 1,
    });
  }

  public onVariantsChange(variants: string[] | null) {
    this.selectedVariants = variants ?? [];
    this.storeParams();
  }

  public isClearFiltersVisible(): boolean {
    return !!(
      this.quoteFilter ||
      this.selectedVariants.length ||
      this.selectedDirection ||
      this.selectedQuoteStatuses.length ||
      this.companyIdsFilter.length ||
      this.createdAtRange?.getStart() ||
      this.createdAtRange?.getEnd() ||
      this.selectedQuoteSources.length
    );
  }

  public clearFilters(): void {
    this.onQuoteFilterChange(null);
    this.onVariantsChange(null);
    this.onDirectionChange(null);
    this.onQuoteStatusSelectChange([]);
    this.onCompanyMultiselectChange([]);
    this.onCreatedAtRangeChange(null);
    this.onQuoteSourcesChange([]);
  }

  public fetchQuotes(): void {
    this.fetchSubscription?.unsubscribe();

    this.loading = true;
    let params = this.gridParams.getHttpParams();
    params = params.delete(quoteListParams.quoteFilter);
    switch (this.quoteFilter) {
      case quoteListFilter.myAgentsQuotes:
        params = params.append(quoteListParams.assignedTo, this.user.getId());
        params = params.append(
          quoteListParams.clientType,
          clientType.Agent.toLowerCase(),
        );
        break;
      case quoteListFilter.myClientQuotes:
        params = params.append(quoteListParams.assignedTo, this.user.getId());
        params = params.append(
          quoteListParams.clientType,
          clientType.Client.toLowerCase(),
        );
        break;
      case quoteListFilter.myQuotes:
        params = params.append(
          quoteListParams.createdOrSentBy,
          this.user.getId(),
        );
        break;
      case quoteListFilter.myTeamsQuotes:
        params = params.append(quoteListParams.myTeamsQuotes, true);
        break;
    }

    this.resetPendingQuoteTimeout();

    this.fetchSubscription = this.quoteRepository
      .getQuoteList(params)
      .subscribe({
        next: (quoteList) => {
          this.quoteList = quoteList;
          this.checkPendingQuoteTimeout();
        },
        error: (err) => {
          this.notifyService.toast(
            TOAST.ERROR,
            "Quote list has not been fetched",
            new BackendErrorPipe().transform(err),
          );
        },
        complete: () => (this.loading = false),
      });
    this.subscription.add(this.fetchSubscription);
  }

  public onSaveComment(quote: Quote) {
    const payload = {
      discriminator: quote.getQuoteType(),
      internalComment: quote.getInternalComment(),
    };

    this.subscription.add(
      this.quoteRepository.patchQuote(quote.getId(), payload).subscribe({
        next: (updatedQuote) => {
          this.notifyService.toast(TOAST.SUCCESS, "Quote has been updated");
        },
        error: (err) => {
          this.notifyService.toast(TOAST.ERROR, "Error");
        },
      }),
    );
  }

  public clearSelection() {
    this.selectedQuotes = [];
    for (let i = 0; i < this.quoteList.items.length; i++) {
      this.quoteList.items[i].requestInfo = null;
    }
  }

  // Adds the current page only: the other pages are not loaded.
  public selectAll() {
    this.selectedQuotes = TableSelectionBarComponent.selectPage(
      this.selectedQuotes,
      this.quoteList.items,
    );
  }

  public requestInfoChange(event: { key: number; value: IRequestInfo }) {
    for (let i = 0; i < this.quoteList.items.length; i++) {
      if (this.quoteList.items[i].getId() === event.key) {
        this.quoteList.items[i].requestInfo = event.value;
      }
    }
  }

  public selectionChange(selectedQuotes: Quote[]) {
    this.selectedQuotes = selectedQuotes;
  }

  public toggleBatchEditor() {
    const editor = this.batchEditor();
    if (editor.isOpened()) {
      editor.close();
    } else {
      editor.open();
    }
  }

  public onBatchSend(event: {
    validityDates: DateRange;
    quoteEmail: QuoteEmail;
  }) {
    this.batchEditor()?.close();
    this.batchActions()?.batchSend(event);
  }

  public batchSendComplete() {
    const hasError = this.quoteList.items.some(
      (quote) => quote.requestInfo?.type === requestInfoType.error,
    );

    if (hasError) {
      return;
    }

    this.fetchQuotes();
  }

  private resetPendingQuoteTimeout() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }
  }

  private checkPendingQuoteTimeout() {
    for (let i = 0; i < this.quoteList.items.length; i++) {
      if (this.quoteList.items[i].getStatus() === this.quoteStatus.pending) {
        this.refreshTimeout = setTimeout(() => {
          this.fetchQuotes();
        }, 60000);

        return;
      }
    }
  }

  private loadClientParams() {
    const client = this.client();
    if (client) {
      const urlFilters = this.pickPreservedUrlParams();
      this.resetGridParamsOptions();
      this.gridParams.setParams(urlFilters, false);
      this.gridParams.setParam("criteria[client]", client.getId().toString());
    } else {
      this.gridParams.unsetParam("criteria[client]");
    }
  }

  // A dashboard drill-down carries its filter on the URL; only those keys survive
  // the client-mode reset, so a stale stored filter still cannot leak into the tab.
  private pickPreservedUrlParams(): { [key: string]: string } {
    const preserved: { [key: string]: string } = {};

    QuoteListComponent.CLIENT_MODE_URL_FILTERS.forEach((key) => {
      if (
        Object.prototype.hasOwnProperty.call(this.gridParams.urlParams, key)
      ) {
        preserved[key] = this.gridParams.urlParams[key];
      }
    });

    return preserved;
  }

  private loadParams() {
    this.searchQuery = this.gridParams.getParam(quoteListParams.search);
    this.quoteFilter = this.gridParams.getParam(quoteListParams.quoteFilter);

    this.selectedVariants =
      this.gridParams.getParam(quoteListParams.variantsFilter) ?? [];
    const statusParam = this.gridParams.getParam(quoteListParams.quoteStatus);
    this.selectedQuoteStatuses = statusParam ? statusParam.split(",") : [];
    this.selectedDirection = this.gridParams.getParam(
      quoteListParams.direction,
    );
    this.companyIdsFilter = this.gridParams.getParam("criteria[companies]")
      ? this.gridParams
          .getParam("criteria[companies]")
          .split(",")
          .map((id) => Number(id))
      : [];

    const sourceParam = this.restoreSourceParam();
    this.selectedQuoteSources = sourceParam
      ? sourceParam
          .split(",")
          .map((source) =>
            source === QuoteListComponent.NULL_QUOTE_SOURCE ? null : source,
          )
      : [];

    const fromParam = this.gridParams.getParam(quoteListParams.createdAtFrom);
    const toParam = this.gridParams.getParam(quoteListParams.createdAtTo);
    const from = fromParam ? this.parseLocalDate(fromParam) : null;
    const to = toParam ? this.parseLocalDate(toParam) : null;
    this.createdAtRange = from || to ? new DateRange(from, to) : null;
  }

  private storeParams() {
    if (this.user) {
      this.gridParams.setParams({
        [quoteListParams.quoteFilter]: this.quoteFilter,
        [quoteListParams.variantsFilter]: this.selectedVariants.length
          ? this.selectedVariants
          : null,
        [quoteListParams.quoteStatus]: this.selectedQuoteStatuses.length
          ? this.selectedQuoteStatuses.join(",")
          : null,
        [quoteListParams.direction]: this.selectedDirection,
        [quoteListParams.page]: 1,
      });
    }
  }

  private openInNewTab(quote: Quote): void {
    window.open(`/quote/${quote.getId()}/${quote.getDisplayId()}`);
  }

  private resetGridParamsOptions() {
    Object.keys(this.gridParams.params).forEach((key) => {
      if (!this.gridParams.isDefaultParam(key)) {
        this.gridParams.unsetParam(key);
      }
    });
  }
}
